import { Component, OnInit } from '@angular/core'
import { Location } from '@angular/common'
import { ActivatedRoute, Router } from '@angular/router'
import { MatDialog } from '@angular/material/dialog'
import { take } from 'rxjs/operators'
import { LeaderService } from 'src/app/leader-toolkit/services/leader.service'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { ApplicationArea } from 'src/app/shared/services/ppv.api'
import { CreateNewTaskComponent } from '../../tasks/create-new-task/create-new-task.component'
import { ViewTaskModalComponent } from '../../tasks/view-task-modal/view-task-modal.component'
import { NewMeetingSchedulerComponent } from '../../scheduling/new-meeting-scheduler/new-meeting-scheduler.component'
import { MeetingDetailDialogComponent } from '../../scheduling/meeting-detail-dialog.component'
import {
   HospitalDashboardData,
   HospitalDashboardGoal,
   HospitalDashboardTask,
   HospitalGoalTask,
   HospitalMeeting,
   NextQuarterGoal,
} from 'src/app/leader-toolkit/models/hospital-dashboard.model'

@Component({
   selector: 'vc-hospital-dashboard',
   templateUrl: './hospital-dashboard.component.html',
   styleUrl: './hospital-dashboard.component.scss',
   standalone: false,
})
export class HospitalDashboardComponent implements OnInit {
   isLoading = true

   selectedHospitalId = 0
   hospitalList: any[] = []

   quarters: any[] = []
   selectedQuarter = ''

   dashboard: HospitalDashboardData | null = null
   expandedGoalTargetIds: Set<number> = new Set()
   highlightedGoalTargetIds: Set<number> = new Set()
   isHM = false
   /** True when the signed-in user is an actual Hospital Manager. */
   isActualHM = false
   /** True when a non-HM user has enabled the "View as HM" preview. */
   previewAsHM = false
   currentUserName = ''
   currentUserId: number | null = null

   get canPreviewAsHM(): boolean {
      return !this.isActualHM
   }

   /** Deep link (?taskId / ?goalTargetId / ?meetingId) captured from the URL, opened once data is loaded. */
   private pendingDeepLink: { taskId?: number; goalTargetId?: number; meetingId?: number } = {}
   private deepLinkApplied = false

   constructor(
      private leaderService: LeaderService,
      private router: Router,
      private route: ActivatedRoute,
      private location: Location,
      private dialog: MatDialog,
      private authMgr: AuthManager
   ) {}

   ngOnInit(): void {
      this.loadPreviewFromUrl()
      this.loadMeta()
      this.authMgr.getCurrentUser().subscribe((user: any) => {
         this.currentUserName = user?.displayName || user?.name || '';
         this.currentUserId = user?.id ?? null
         this.isActualHM = (user?.roles || []).some(
            (r: any) => r?.application === ApplicationArea.LTK && r?.roleName === 'HM'
         )
         this.isHM = this.isActualHM || this.previewAsHM
         // An HM cannot preview a preview; collapse any stale state.
         if (this.isActualHM) {
            this.previewAsHM = false
         }
      })
      this.captureDeepLink()
   }

   /** Restores "View as HM" when the page is reloaded/deep-linked with ?viewAsHm=true. */
   private loadPreviewFromUrl(): void {
      this.route.queryParams.pipe(take(1)).subscribe((params) => {
         if (String(params['viewAsHm']).toLowerCase() === 'true') {
            this.previewAsHM = true
            this.isHM = true
         }
      })
   }

   /** Toggles the HM preview for non-HM users and reloads the dashboard with HM scoping. */
   togglePreviewAsHM(): void {
      if (this.isActualHM) return
      this.previewAsHM = !this.previewAsHM
      this.isHM = this.isActualHM || this.previewAsHM
      this.router.navigate([], {
         queryParams: { viewAsHm: this.previewAsHM ? true : null },
         queryParamsHandling: 'merge',
         replaceUrl: true,
      })
      this.loadDashboardData()
   }

   /**
    * Reads taskId / goalTargetId / meetingId query params (email notification links and
    * redirected deep links) and strips them from the URL; the matching task/meeting dialog
    * is opened, or the goal accordion is expanded, once the dashboard data has loaded.
    */
    private captureDeepLink(): void {
       this.route.queryParams.pipe(take(1)).subscribe((params) => {
          const taskId = Number(params['taskId'])
          const goalTargetId = Number(params['goalTargetId'])
          const meetingId = Number(params['meetingId'])
          const hospitalId = Number(params['hospitalId'])

          if (Number.isFinite(hospitalId) && hospitalId > 0) this.selectedHospitalId = hospitalId
          if (Number.isFinite(taskId) && taskId > 0) this.pendingDeepLink.taskId = taskId
          if (Number.isFinite(goalTargetId) && goalTargetId > 0) this.pendingDeepLink.goalTargetId = goalTargetId
          if (Number.isFinite(meetingId) && meetingId > 0) this.pendingDeepLink.meetingId = meetingId

          if (this.pendingDeepLink.taskId || this.pendingDeepLink.goalTargetId || this.pendingDeepLink.meetingId) {
             this.router.navigate([], {
                queryParams: { taskId: null, goalTargetId: null, meetingId: null, hospitalId: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
             })
          }
       })
    }

   /** Opens the dialog(s) / expands the goal accordion for a captured deep link, once per navigation. */
   private applyPendingDeepLink(): void {
      if (this.deepLinkApplied) return
      const { taskId, goalTargetId, meetingId } = this.pendingDeepLink
      if (!taskId && !goalTargetId && !meetingId) return
      this.deepLinkApplied = true

      if (taskId) {
         this.openTask(taskId)
      }
      if (goalTargetId) {
         // HM users don't have the Goals page, so instead of opening the goal dialog,
         // expand the goal accordion and scroll to the matching goal target.
         this.focusGoal(goalTargetId)
      }
      if (meetingId) {
         const meeting =
            (this.dashboard?.upcomingMeetings || []).find((m) => m.meetingId === meetingId) ||
            (this.dashboard?.nextMeeting?.meetingId === meetingId ? this.dashboard.nextMeeting : null)
         if (meeting) {
            this.openMeetingDetailDialog(meeting)
         }
      }
   }

   /** Loads hospital list + quarters first (needed for the header selects), then the dashboard. */
   private loadMeta(): void {
      this.leaderService.getHospitalList(true).subscribe({
         next: (res: any) => {
            const list = res?.data || []
            if (list.length) {
               this.hospitalList = list
               if (!list.some((h: any) => h.practiceSKey === this.selectedHospitalId)) {
                  this.selectedHospitalId = list[0].practiceSKey
               }
            }
            this.loadQuarter()
         },
         error: () => this.loadQuarter(),
      })
   }

   private loadQuarter(): void {
      this.leaderService.getQtrList().subscribe({
         next: (res: any) => {
            const list = res?.data || []
            if (list.length) {
               this.quarters = list
               const current = list.find((q: any) => q.currentQuarter)
               if (current?.quarterName) {
                  this.selectedQuarter = current.quarterName
               }
            }
            this.loadDashboardData()
         },
         error: () => this.loadDashboardData(),
      })
   }

   /**
    * Loads the full dashboard aggregate for the selected hospital + quarter from
    * GET v1/hospitaldashboard/hospital-summary (separate HospitalDashboard module).
    */
   private loadDashboardData(): void {
      this.isLoading = true
      this.leaderService.getHospitalDashboardData(this.selectedHospitalId, this.selectedQuarter, this.previewAsHM).subscribe({
         next: (res: any) => {
            const previouslyExpanded = this.expandedGoalTargetIds
            this.dashboard = this.mapDashboard(res?.data)
            const attentionIds = (this.dashboard?.attention || []).filter((a) => a.goalTargetId).map((a) => a.goalTargetId as number)
            // Keep cards the HM already had open (e.g. mid-note, mid-task) expanded across a refresh.
            this.expandedGoalTargetIds = new Set(Array.from(previouslyExpanded).concat(attentionIds))
            this.isLoading = false
            this.applyPendingDeepLink()
         },
         error: (err) => {
            console.error('Hospital dashboard load failed', err)
            this.dashboard = null
            this.isLoading = false
            this.leaderService.showNotification('Failed to load hospital dashboard', true)
            this.applyPendingDeepLink()
         },
      })
   }

   onHospitalChange(): void {
      this.loadDashboardData()
   }

   onQuarterChange(): void {
      this.loadDashboardData()
   }

   locationBack(): void {
      this.location.back()
   }

   get attentionHeadline(): string {
      const goalItems = (this.dashboard?.attention || []).filter((a) => a.goalTargetId).length
      if (goalItems) {
         return goalItems + (goalItems === 1 ? ' goal needs' : ' goals need') + ' your attention this week.'
      }
      const taskItems = (this.dashboard?.attention || []).filter((a) => a.linkTo === 'tasks').length
      return taskItems ? taskItems + (taskItems === 1 ? ' task is' : ' tasks are') + ' past due.' : "You're on track across all goals this week."
   }

   // ── Child component event handlers ────────────────────────────────────────

   toggleGoal(goalTargetId: number): void {
      if (this.expandedGoalTargetIds.has(goalTargetId)) {
         this.expandedGoalTargetIds.delete(goalTargetId)
      } else {
         this.expandedGoalTargetIds.add(goalTargetId)
      }
   }

   focusGoal(goalTargetId: number): void {
      this.expandedGoalTargetIds.add(goalTargetId)
      this.highlightedGoalTargetIds.add(goalTargetId)
      // Let the newly expanded card paint before scrolling.
      setTimeout(() => {
         document.getElementById('hospital-goal-' + goalTargetId)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
      setTimeout(() => {
         this.highlightedGoalTargetIds.delete(goalTargetId)
      }, 2000)
   }

   scrollToTasks(): void {
      setTimeout(() => {
         document.getElementById('hospital-tasks-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
   }

   addTask(): void {
      this.openTaskDialog()
   }

   viewAllTasks(): void {
      // HM users are restricted to this dashboard, so there is no tasks page to open.
      if (this.isHM) return
      this.router.navigate(['/leader-toolkit-tasks'])
   }

   openTask(taskId: number): void {
      const dialogRef = this.dialog.open(ViewTaskModalComponent, {
         data: { taskId, page: 'tasks' },
         width: '600px',
         disableClose: true,
      })
      dialogRef.afterClosed().subscribe((result) => {
         if (result?.success) {
            this.loadDashboardData()
         }
      })
   }

    /** Fired from both the Tasks panel (raw task) and a goal card ({ goal, task }) — same task record either way. */
   completeTask(payload: HospitalDashboardTask | HospitalGoalTask | { goal: HospitalDashboardGoal; task: HospitalGoalTask }): void {
      const task: any = (payload as any)?.task ?? payload
      const taskId = task?.taskId
      if (!taskId) return
      this.leaderService.completeTask({ taskId }).subscribe({
         next: () => {
            this.leaderService.showNotification('Task completed successfully!', false)
            this.leaderService.clearTaskListCache()
            this.loadDashboardData()
         },
         error: () => this.leaderService.showNotification('Failed to complete task', true),
      })
   }

   addMeeting(): void {
      const selectedHospital = this.hospitalList.find((h) => h.practiceSKey === this.selectedHospitalId) || null
      const dialogRef = this.dialog.open(NewMeetingSchedulerComponent, {
         data: {
            hospitals: selectedHospital ? [selectedHospital] : this.hospitalList,
         },
         width: '680px',
         disableClose: true,
      })
      dialogRef.afterClosed().subscribe((result) => {
         if (result?.status === 'success' || result?.refreshMeetings) {
            this.loadDashboardData()
         }
      })
   }

   joinMeeting(meeting: any): void {
      if (meeting?.joinUrl) {
         window.open(meeting.joinUrl, '_blank')
      }
   }

   openMeetingDetailDialog(meeting: HospitalMeeting): void {
      this.dialog.open(MeetingDetailDialogComponent, {
         width: '500px',
         data: meeting
      })
   }

   addGoalTask(goal: HospitalDashboardGoal): void {
      this.openTaskDialog(goal)
   }

   onPostNote(event: { goal: HospitalDashboardGoal; text: string; isHMVisible?: boolean }): void {
      const goalTargetId = event?.goal?.goalTargetId
      const text = event?.text?.trim()
      if (!goalTargetId || !text) return
      this.leaderService.createGoalComment({ goaltargetid: goalTargetId, comments: text, isHMVisible: event.isHMVisible ?? true }).subscribe({
         next: (res: any) => {
            if (res?.resultDetails?.[0]?.status === 'SUCCESS') {
               this.leaderService.showNotification('Note posted', false)
               this.loadDashboardData()
            } else {
               this.leaderService.showNotification(res?.resultDetails?.[0]?.message || 'Failed to post note', true)
            }
         },
         error: () => this.leaderService.showNotification('Failed to post note', true),
      })
   }

   onUpdateNote(event: { goal: HospitalDashboardGoal; note: any; text: string; isHMVisible: boolean }): void {
      const goalTargetId = event?.goal?.goalTargetId;
      const commentId = event?.note?.approvalId || event?.note?.commentId || event?.note?.id;
      const text = event?.text?.trim();
      if (!goalTargetId || !commentId || !text) return;
      this.leaderService.updateGoalComment({
         goalTargetId,
         commentId,
         comments: text,
         isHMVisible: event.isHMVisible
      }).subscribe({
         next: (res: any) => {
            if (res?.resultDetails?.[0]?.status === 'SUCCESS') {
               this.leaderService.showNotification('Comment updated successfully!', false);
               this.loadDashboardData();
            } else {
               this.leaderService.showNotification(res?.resultDetails?.[0]?.message || 'Failed to update comment', true);
            }
         },
         error: () => this.leaderService.showNotification('Failed to update comment', true),
      });
   }

   onDeleteNote(event: { goal: HospitalDashboardGoal; note: any }): void {
      const goalTargetId = event?.goal?.goalTargetId;
      const commentId = event?.note?.approvalId || event?.note?.commentId || event?.note?.id;
      if (!goalTargetId || !commentId) return;
      this.leaderService.deleteGoalComment({ goalTargetId, commentId }).subscribe({
         next: (res: any) => {
            if (res?.resultDetails?.[0]?.status === 'SUCCESS') {
               this.leaderService.showNotification('Comment deleted successfully!', false);
               this.loadDashboardData();
            } else {
               this.leaderService.showNotification(res?.resultDetails?.[0]?.message || 'Failed to delete comment', true);
            }
         },
         error: () => this.leaderService.showNotification('Failed to delete comment', true),
      });
   }

   /** Opens the shared CreateNewTaskComponent dialog, pre-scoped to this hospital/quarter (and goal, if given). */
   private openTaskDialog(goal?: HospitalDashboardGoal): void {
      const dialogRef = this.dialog.open(CreateNewTaskComponent, {
         data: {
            hospital: this.hospitalList.find((h) => h.practiceSKey === this.selectedHospitalId) || null,
            quarter: this.quarters.find((q) => q.quarterName === this.selectedQuarter) || null,
            goal: goal ? { goalTargetId: goal.goalTargetId, goalName: goal.goalName } : null,
         },
      })
      dialogRef.afterClosed().subscribe((result) => {
         if (result === 'success') {
            this.loadDashboardData()
         }
      })
   }

   // ── API response → frontend model mapping ─────────────────────────────────

   private mapDashboard(d: any): HospitalDashboardData {
      return {
         hospitalName: d?.hospitalName || '',
         quarter: d?.quarter || this.selectedQuarter,
         weeksRemaining: d?.weeksRemaining ?? 0,
         refreshedAt: this.formatRefreshed(d?.refreshedAt),
         stats: (d?.stats || []).map((s: any) => ({ ...s })),
         attention: (d?.attention || []).map((a: any) => ({ ...a })),
         nextMeeting: d?.nextMeeting ? this.mapMeeting(d.nextMeeting) : null,
         upcomingMeetings: (d?.upcomingMeetings || []).map((m: any) => this.mapMeeting(m)),
         pastDueTasks: (d?.pastDueTasks || []).map((t: any) => this.mapTask(t)),
         upcomingTasks: (d?.upcomingTasks || []).map((t: any) => this.mapTask(t)),
         goals: (d?.goals || []).map((g: any) => this.mapGoal(g)),
         nextQuarterGoals: (d?.nextQuarterGoals || []).map((g: any) => this.mapNextQuarterGoal(g)),
      }
   }

   private mapGoal(g: any): HospitalDashboardGoal {
      return {
         goalTargetId: g?.goalTargetId,
         goalName: g?.goalName || '',
         goalNumber: g?.goalNumber || '',
         owner: g?.owner || '',
         dateRange: g?.dateRange || '',
         status: g?.status || 'At_Risk',
         momentum: g?.momentum || 'NONE',
         pacePercent: g?.pacePercent ?? null,
         progressPercent: g?.progressPercent ?? 0,
         quarterElapsedPercent: g?.quarterElapsedPercent ?? 0,
         currentValue: g?.currentValue ?? null,
         targetValue: g?.targetValue ?? null,
         runRatePerWeek: g?.runRatePerWeek ?? null,
         runRateNote: g?.runRateNote || '',
         gap: g?.gap ?? null,
         recommendedType: g?.recommendedType || '',
         comparisons: (g?.comparisons || []).map((c: any) => ({ ...c })),
         resources: g?.resources || [],
         trajectory: g?.trajectory || { categories: [], actual: [], required: [], valueFormat: 'number' },
         tasks: (g?.tasks || []).map((t: any) => this.mapGoalTask(t)),
         notes: (g?.notes || []).map((n: any) => ({ 
            ...n,
            approvalId: n?.approvalId || n?.commentId || n?.id,
            commentId: n?.commentId || n?.approvalId || n?.id,
            isHMVisible: n?.isHMVisible !== undefined ? n?.isHMVisible : (n?.visibleToHM !== undefined ? n?.visibleToHM : true),
         })),
         uom: g?.uom || undefined,
      }
   }

   private mapTask(t: any): HospitalDashboardTask {
      return {
         taskId: t?.taskId,
         title: t?.title || '',
         dueDate: t?.dueDate,
         assignedTo: t?.assignedTo || '',
         goalName: t?.goalName || '',
         status: t?.status || 'open',
         daysPastDue: t?.daysPastDue,
      }
   }

   private mapGoalTask(t: any): HospitalGoalTask {
      return {
         taskId: t?.taskId,
         title: t?.title || '',
         dueDate: t?.dueDate,
         assignedTo: t?.assignedTo || '',
         goalName: t?.goalName || '',
         status: t?.status || 'open',
         daysPastDue: t?.daysPastDue,
         completedDate: t?.completedDate,
      }
   }

   private mapMeeting(m: any): HospitalMeeting {
      return {
         meetingId: m?.meetingId,
         meetingSubject: m?.meetingSubject || '',
         hospitalName: m?.hospitalName || '',
         scheduledDate: m?.scheduledDate,
         startTime: this.formatTime(m?.startTime),
         endTime: this.formatTime(m?.endTime),
         duration: m?.duration || '',
         timeZone: m?.timeZone || '',
         joinUrl: m?.joinUrl || '',
         attendeesNames: m?.attendeesNames || [],
      }
   }

   private mapNextQuarterGoal(g: any): NextQuarterGoal {
      return {
         goalTargetId: g?.goalTargetId,
         goalName: g?.goalName || '',
         dateRange: g?.dateRange || '',
         reviewedOn: g?.reviewedOn || 'TBD',
         target: g?.target || '',
         targetLabel: g?.targetLabel || '',
         baseline: g?.baseline || '',
         lastQuarter: g?.lastQuarter || '',
         resources: g?.resources || [],
      }
   }

   /** "13:00" -> "1:00 PM" (pass through anything already formatted). */
   private formatTime(t?: string): string {
      if (!t) return ''
      const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim())
      if (!m) return t
      let h = parseInt(m[1], 10)
      const min = m[2]
      const ampm = h >= 12 ? 'PM' : 'AM'
      h = h % 12 || 12
      return `${h}:${min} ${ampm}`
   }

   /** ISO UTC string -> locale-ish "8/18/2026, 6:00 AM". */
   private formatRefreshed(iso?: string): string {
      if (!iso) return ''
      const dt = new Date(iso)
      if (isNaN(dt.getTime())) return iso
      return dt.toLocaleString('en-US', {
         month: 'numeric',
         day: 'numeric',
         year: 'numeric',
         hour: 'numeric',
         minute: '2-digit',
      })
   }
}
